import { type CardListItem, CardNotFound, type CardPage, type CardSummary } from "@hobbyshopify/backend-client";
import { hasError } from "operation-result.js";
import { SubtreeController } from "subtree.js/core";
import type { AppDeps } from "../../app-deps";
import {
  archiveCard,
  CARDS_PAGE_MAX,
  CARDS_PAGE_SIZE,
  type CardListFilter,
  type CatalogueKind,
  loadCard,
  loadCards,
  repriceCard,
  setCardPrice,
  setCardStock,
} from "../../core/ops/cards-ops";
import { loadReviewQueue } from "../../core/ops/review-ops";
import {
  ARCHIVE_FAILED_ON_THE_WAY,
  type CardSection,
  REPRICE_FAILED_ON_THE_WAY,
} from "../../core/primitives/card-edit";
import { errorText } from "../../core/primitives/errors";
import { loaded } from "../../core/primitives/load-status";
import { Loader } from "../../core/primitives/loader";
import { isScanning } from "../../core/primitives/review-row";
import { displayedAvailable, onHandFor, STOCK_LIST_REASON, stockValueCents } from "../../core/primitives/stock-list";
import { can } from "../../core/primitives/store-role";
import type { StockListPlace } from "../../core/repositories/stock-list-repository";
import {
  CardsActions,
  CardsState,
  priceChangeText,
  type RepriceOutcome,
  type StockUndo,
  stockChangeText,
  stockFailedText,
  stockTitle,
  UNDO_FAILED_ON_THE_WAY,
} from "./cards-model";

export type CardsDeps = Pick<
  AppDeps,
  "client" | "authHandler" | "storeRepository" | "reviewQueueRepository" | "stockListRepository"
>;

export interface CardsRouting {
  /** `place` is the id the list was kept under for Back, which the Stock entry left carries. */
  openCard(cardId: string, section: CardSection | null, place: string | null): void;
  /** Writes the filter and the search into the URL, so Back from a card lands on the same list. */
  showQuery(filter: CardListFilter, search: string, kind: CatalogueKind): void;
}

/** How long Undo stays offered, as on iOS. */
export const UNDO_MS = 5000;

/** How long opening a card waits for the page's writes to land, so the card page and Back read them. */
export const OPEN_WAITS_MS = 1500;

interface CardsQuery {
  readonly shopId: string;
  readonly filter: CardListFilter;
  readonly kind: CatalogueKind;
  readonly search: string;
  /** Set when fetching the page after the one shown: the answer appends rather than replaces. */
  readonly cursor?: string;
  /** When the read went out, set as it does: against the reads of single cards, the later-sent answer wins. */
  asked: number;
  /** How many cards to read; a page's worth when unset. */
  readonly limit?: number;
}

/** A write's answer, laid over a card's copy until a read sent after it covers it. */
interface Laid {
  /** When the answer came: a read sent after it saw the write. */
  readonly at: number;
  readonly change: (card: CardSummary) => CardSummary;
}

/** What is known of a card: the newest read's copy (null once it is gone) and the writes answered since. */
interface Known {
  /** When that read went out. */
  readonly asked: number;
  readonly read: CardListItem | null;
  readonly laid: readonly Laid[];
}

/** The copy a row shows: the newest read, with the writes answered since laid over it. */
function shownOf(known: Known): CardListItem | null {
  if (known.read === null) return null;
  let card = known.read.card;
  for (const l of known.laid) card = l.change(card);
  return card === known.read.card ? known.read : { ...known.read, card };
}

/** Rows held until a list read sent after `after` lands: those named, or every row (`null`). */
interface Awaiting {
  /** Infinity while the writes it waits for are still on their way. */
  after: number;
  readonly cards: ReadonlySet<string> | null;
  readonly release: () => void;
}

/** One card's counts, from the first tap until the last one sent lands with no tap waiting. */
interface StockSeries {
  /** The card as the row showed it at the first tap: every count of the series is sent against it. */
  readonly base: Pick<CardSummary, "onHand" | "available">;
  /** What a count lost on its way calls the card. */
  readonly title: string;
  /** The newest count sent, or waiting behind one on its way. */
  counted: number | null;
  /** Numbers the counts sent, so only the newest one's answer ends the series. */
  sent: number;
}

interface StockSave {
  readonly shopId: string;
  readonly cardId: string;
  /** The count the row shows, which can be sold. */
  readonly target: number;
  readonly series: StockSeries;
  readonly sent: number;
}

/** When the Undo window ends, and what ending it does; held while the bar has the focus or the pointer. */
interface UndoWindow {
  endsAt: number;
  left: number;
  held: boolean;
  readonly ended: () => void;
}

const without = <T>(record: Readonly<Record<string, T>>, key: string): Record<string, T> => {
  const { [key]: _dropped, ...rest } = record;
  return rest;
};
const adding = (set: ReadonlySet<string>, key: string) => new Set([...set, key]);
const removing = (set: ReadonlySet<string>, key: string) => new Set([...set].filter((k) => k !== key));
/** Settles, never rejects, once every write given is answered or lost. */
const landed = (writes: readonly (Promise<unknown> | undefined)[]): Promise<void> =>
  Promise.allSettled(writes).then(() => undefined);

/**
 * The shop's catalogue, as iOS's Stock pane: a count changed on the row, a price
 * looked up again on the row, each with five seconds to undo, and everything
 * else on the card page.
 */
export class CardsController extends SubtreeController implements CardsActions {
  readonly state = new CardsState();
  readonly #list: Loader<CardsQuery, CardPage>;
  #shopId: string | null = null;
  #asked = 0;
  /** What is known of each card, from a page or a read of the card alone, and the writes answered since. */
  readonly #known = new Map<string, Known>();
  #undoTimer: ReturnType<typeof setTimeout> | null = null;
  #undoWindow: UndoWindow | null = null;
  readonly #series = new Map<string, StockSeries>();
  /** The save running per card, so a second waits for the first. */
  readonly #stockSaves = new Map<string, Promise<void>>();
  /** The lookups, archives and price undos on their way, per card. */
  readonly #writes = new Map<string, Promise<void>>();
  /** The filter and search the list on screen was read for: Load more extends that list only. */
  #shown: { filter: CardListFilter; kind: CatalogueKind; search: string } | null = null;
  /** Where Back left the list, until a read of it lands: Retry goes back there. */
  #place: { y: number; cardId: string } | null = null;
  /** How many holds each row has: archives, prices put back, writes another page left. */
  readonly #holds = new Map<string, number>();
  /** Bumped when the shop changes, so a hold taken for the last shop lets nothing go here. */
  #holdsOf = 0;
  /** Holds a list read lets go once it is asked after what they wait for. */
  #awaiting: Awaiting[] = [];
  /** Counts Back showed as the rows had them, until a read covers their cards. */
  readonly #restoredEdits = new Set<string>();

  constructor(
    readonly deps: CardsDeps,
    readonly routing: CardsRouting,
    initial: { filter?: CardListFilter; kind?: CatalogueKind; search?: string; place?: string | null } = {},
  ) {
    super();
    this.state.filter.value = initial.filter ?? "all";
    this.state.kind.value = initial.kind ?? "all";
    this.state.search.value = initial.search ?? "";
    this.subtree.put(CardsState, this.state);
    this.subtree.put(CardsActions, this);
    this.#list = this.own(
      new Loader({
        status: this.state.status,
        // The writes on their way land first, or the page shows the counts from before them.
        fetch: (query: CardsQuery) => {
          const { shopId, filter, kind, search, cursor, limit } = query;
          const read = () => {
            query.asked = ++this.#asked;
            return loadCards({
              ...deps,
              shopId,
              filter,
              kind,
              search,
              ...(cursor ? { cursor } : {}),
              ...(limit ? { limit } : {}),
            });
          };
          const waiting = this.#writesLanded(shopId);
          return waiting ? waiting.then(read) : read();
        },
        loaded: (page, query) => this.#landPage(page, query),
      }),
    );

    // Back from a card shows the list as it was left, held until it is read again; any other way in reads it.
    let placeId = initial.place ?? null;
    // The shell guarantees a shop before this page renders, but the selection can still change.
    this.sync(() => {
      const shopId = deps.storeRepository.shopId;
      if (shopId === this.#shopId) return;
      // A count waiting for its window goes to the shop it was counted in.
      this.#endUndo();
      this.#shopId = shopId;
      this.#known.clear();
      this.#series.clear();
      this.#shown = null;
      this.state.items.value = [];
      this.state.nextCursor.value = null;
      this.state.stockEdits.value = {};
      this.state.outcomes.value = {};
      this.state.repricing.value = new Set();
      this.#holdsOf++;
      this.#holds.clear();
      this.#awaiting = [];
      this.#restoredEdits.clear();
      this.state.busy.value = new Set();
      this.state.refreshing.value = false;
      this.state.error.value = null;
      this.#place = null;
      const place = placeId === null ? null : deps.stockListRepository.place(placeId);
      placeId = null;
      if (
        place?.shopId === shopId &&
        place.filter === this.state.filter.value &&
        place.kind === this.state.kind.value &&
        place.search === this.state.search.value
      )
        this.#restore(place);
      else void this.#load();
    }, [deps.storeRepository]);
    // Null role (not yet resolved) behaves like today — allowed — until it is known.
    this.sync(() => {
      const role = deps.storeRepository.selectedStore?.myRole ?? null;
      this.state.canEditInventory.value = role === null || can(role, "inventoryEdit");
    }, [deps.storeRepository]);
    this.sync(() => {
      const repo = deps.reviewQueueRepository;
      // As Review counts them: a card still in the pipeline is no row there yet.
      const now = new Date();
      this.state.reviewCount.value = repo.groups
        .filter((g) => !isScanning(g.sample, now))
        .reduce((n, g) => n + g.count, 0);
      const shopId = deps.storeRepository.shopId;
      if (shopId !== null && (repo.status.kind === "idle" || repo.isStale))
        void loadReviewQueue({ ...deps, shopId, locationId: deps.storeRepository.locationId });
    }, [deps.reviewQueueRepository, deps.storeRepository]);
    this.sync(() => {
      this.state.stockValueCents.value = stockValueCents(
        this.state.items.value,
        this.state.stockEdits.value,
        this.state.nextCursor.value === null,
      );
    }, [this.state.items, this.state.stockEdits, this.state.nextCursor]);
    // Leaving the page is the merchant moving on, not undoing: a waiting count is sent.
    this.autoDispose(() => this.#leave());
  }

  setFilter = (filter: CardListFilter) => {
    this.state.filter.value = filter;
    this.#place = null;
    this.routing.showQuery(filter, this.state.search.value, this.state.kind.value);
    void this.#load();
  };

  setKind = (kind: CatalogueKind) => {
    this.state.kind.value = kind;
    this.#place = null;
    this.routing.showQuery(this.state.filter.value, this.state.search.value, kind);
    void this.#load();
  };

  setSearch = (search: string) => {
    this.state.search.value = search;
    this.#place = null;
    this.routing.showQuery(this.state.filter.value, search, this.state.kind.value);
    const query = this.#query();
    if (!query) return;
    // Typing waits for the word to settle; clearing the box answers at once.
    if (search) this.#list.loadDebounced(query);
    else void this.#list.load(query);
  };

  loadMore = async () => {
    const cursor = this.state.nextCursor.value;
    // A list being read again from its first page has no next page yet, nor one whose search is still being typed.
    if (!cursor || this.state.status.value.kind === "loading") return;
    const shown = this.#shown;
    if (
      shown?.filter !== this.state.filter.value ||
      shown.kind !== this.state.kind.value ||
      shown.search !== this.state.search.value
    )
      return;
    const query = this.#query();
    if (query) await this.#list.load({ ...query, cursor });
  };

  retry = () => {
    // Back's rows wait under the failure: the page goes back to the card they were left at, the focus on it.
    if (this.#place !== null && this.state.items.value.length > 0) this.state.scrollTo.value = this.#place;
    void this.#refresh().catch(() => undefined);
  };

  stepStock = (card: CardSummary, step: number) => {
    if (!card.trackStock || this.#shopId === null || !this.#mayWrite(card)) return;
    const undo = this.state.undo.value;
    const held = this.#undoWindow?.held ?? false;
    // A tap on another card sends the waiting count; a price change can no longer be undone.
    if (undo && (undo.kind !== "stock" || undo.cardId !== card.id)) this.#endUndo();
    const edits = this.state.stockEdits.value;
    const shown = displayedAvailable(card, edits);
    const target = Math.max(0, shown + step);
    if (target === shown) return;
    // A count starts as any other change does: what the last one said goes, so a count lost again is said anew.
    this.state.error.value = null;
    const series = this.#series.get(card.id) ?? {
      base: { onHand: card.onHand, available: card.available },
      title: stockTitle(card),
      counted: null,
      sent: 0,
    };
    // The merchant's last count: the newest one sent, else what the row showed at the first tap.
    const from = series.counted ?? series.base.available;
    if (target === from) {
      this.#clearUndoTimer();
      this.state.undo.value = null;
      this.#dropTap(card.id, series);
      return;
    }
    this.#series.set(card.id, series);
    this.state.stockEdits.value = { ...edits, [card.id]: target };
    this.#offerUndo(
      { kind: "stock", cardId: card.id, title: stockTitle(card), change: stockChangeText(from, target) },
      () => {
        void this.#commitStock(card.id);
      },
      held,
    );
  };

  // A call refused here is no write: only one that starts is counted among the writes on their way.
  reprice = (card: CardSummary) => {
    const shopId = this.#shopId;
    if (shopId === null || !this.#mayWrite(card) || this.state.repricing.value.has(card.id)) return Promise.resolve();
    return this.#track(card.id, this.#reprice(shopId, card));
  };

  archive = (card: CardSummary) => {
    const shopId = this.#shopId;
    // Not while its price is looked up: the lookup's answer would offer an Undo for a card that is gone.
    if (shopId === null || !this.#mayWrite(card) || this.state.repricing.value.has(card.id)) return Promise.resolve();
    return this.#track(card.id, this.#archive(shopId, card));
  };

  async #reprice(shopId: string, card: CardSummary): Promise<void> {
    this.#endUndo();
    this.state.repricing.value = adding(this.state.repricing.value, card.id);
    this.state.error.value = null;
    const from = card.currentPriceCents;
    const r = await repriceCard({ ...this.deps, shopId, cardId: card.id }).catch(async (thrown: unknown) => {
      if (!this.#current(shopId)) throw thrown;
      this.state.repricing.value = removing(this.state.repricing.value, card.id);
      this.#setOutcome(card.id, { kind: "failed", message: REPRICE_FAILED_ON_THE_WAY });
      // The lookup may have written a price before the answer was lost.
      await this.#readCard(shopId, card.id).catch(() => false);
      throw thrown;
    });
    if (!this.#current(shopId)) return;
    this.state.repricing.value = removing(this.state.repricing.value, card.id);
    if (!r.ok) {
      this.#setOutcome(card.id, { kind: "failed", message: errorText(r.errors) });
      return;
    }
    const to = r.value;
    if (to == null) this.#setOutcome(card.id, { kind: "none" });
    else {
      this.#setOutcome(card.id, { kind: "found", fromCents: from, toCents: to });
      this.#patch(card.id, (c) => ({ ...c, currentPriceCents: to }));
      // A lookup landing while a card opens offers no Undo: nothing starts on the rows then.
      if (to !== from && this.state.opening.value === null) {
        const held = this.#undoWindow?.held ?? false;
        // A count tapped while the lookup ran is sent rather than replaced.
        this.#endUndo();
        this.#offerUndo(
          {
            kind: "price",
            cardId: card.id,
            title: stockTitle(card),
            change: priceChangeText(from, to),
            fromCents: from,
          },
          () => {},
          held,
        );
      }
    }
    await this.#readCard(shopId, card.id).catch(() => false);
  }

  async #archive(shopId: string, card: CardSummary): Promise<void> {
    this.#endUndo();
    const release = this.#hold(card.id);
    this.state.error.value = null;
    try {
      // A count on its way lands first: the backend refuses a card with copies on hand.
      await this.#stockSaves.get(card.id)?.catch(() => undefined);
      const r = await archiveCard({ ...this.deps, shopId, cardId: card.id }).catch((thrown: unknown) => {
        if (this.#current(shopId)) {
          this.state.error.value = ARCHIVE_FAILED_ON_THE_WAY;
          // A read of the card answers an archived card as any other: the list, which leaves them out, can tell.
          this.#holdUntilListRead(shopId, card.id);
        }
        throw thrown;
      });
      if (!this.#current(shopId)) return;
      if (!r.ok) {
        this.state.error.value = errorText(r.errors);
        return;
      }
      this.#learn(card.id, ++this.#asked, null);
    } finally {
      release();
    }
  }

  undo = async () => {
    const undo = this.state.undo.value;
    if (!undo) return;
    this.#clearUndoTimer();
    this.state.undo.value = null;
    if (undo.kind === "stock") {
      const series = this.#series.get(undo.cardId);
      if (series) this.#dropTap(undo.cardId, series);
      return;
    }
    const shopId = this.#shopId;
    if (shopId === null) return;
    return this.#track(undo.cardId, this.#putPriceBack(shopId, undo));
  };

  async #putPriceBack(shopId: string, undo: Extract<StockUndo, { kind: "price" }>): Promise<void> {
    this.state.outcomes.value = without(this.state.outcomes.value, undo.cardId);
    this.state.error.value = null;
    const release = this.#hold(undo.cardId);
    try {
      const r = await setCardPrice({ ...this.deps, shopId, cardId: undo.cardId, priceCents: undo.fromCents }).catch(
        async (thrown: unknown) => {
          if (this.#current(shopId)) {
            this.state.error.value = UNDO_FAILED_ON_THE_WAY;
            await this.#readCard(shopId, undo.cardId).catch(() => false);
          }
          throw thrown;
        },
      );
      if (!this.#current(shopId)) return;
      if (!r.ok) this.state.error.value = errorText(r.errors);
      else this.#patch(undo.cardId, (c) => ({ ...c, currentPriceCents: undo.fromCents }));
      await this.#readCard(shopId, undo.cardId).catch(() => false);
    } finally {
      release();
    }
  }

  openCard = (card: CardSummary, section?: CardSection, scrollY = 0) => {
    // One at a time: a second tap while the first waits for its card's writes would open the card twice.
    if (this.state.opening.value !== null) return;
    this.#endUndo();
    const open = () => {
      const shopId = this.#shopId;
      const shown = this.#shown;
      const place =
        shopId !== null && shown !== null
          ? this.deps.stockListRepository.remember({
              shopId,
              filter: shown.filter,
              kind: shown.kind,
              search: shown.search,
              items: this.state.items.value,
              nextCursor: this.state.nextCursor.value,
              scrollY,
              openedCardId: card.id,
              stockEdits: this.state.stockEdits.value,
            })
          : null;
      // The card page reads the ledger as it opens, before this page goes: its writes still on their way go there now.
      const write = this.#onTheWay().get(card.id);
      if (write) this.deps.stockListRepository.leaving(card.id, write);
      this.routing.openCard(card.id, section ?? null, place);
    };
    // The card page reads the card: its writes on their way, this page's and those pages left, land first.
    // Back reads the whole list again, so the other cards' writes need no wait here.
    const writes = [this.#onTheWay().get(card.id), this.deps.stockListRepository.left.get(card.id)].filter(
      (write) => write !== undefined,
    );
    if (writes.length === 0) {
      open();
      return;
    }
    this.state.opening.value = card.id;
    const shopId = this.#shopId;
    let opened = false;
    const once = () => {
      if (opened || this.isDisposed) return;
      opened = true;
      this.state.opening.value = null;
      // Another shop chosen meanwhile: the card is not this list's any more.
      if (this.#shopId === shopId) open();
    };
    void landed(writes).then(once);
    setTimeout(once, OPEN_WAITS_MS);
  };

  placeRestored = () => {
    this.state.scrollTo.value = null;
  };

  holdUndo = () => {
    const undoWindow = this.#undoWindow;
    if (!undoWindow || undoWindow.held) return;
    undoWindow.held = true;
    undoWindow.left = Math.max(0, undoWindow.endsAt - Date.now());
    if (this.#undoTimer !== null) clearTimeout(this.#undoTimer);
    this.#undoTimer = null;
  };

  releaseUndo = () => {
    const undoWindow = this.#undoWindow;
    if (!undoWindow?.held) return;
    undoWindow.held = false;
    this.#runUndoWindow(undoWindow, undoWindow.left);
  };

  dismissError = () => {
    this.state.error.value = null;
  };

  /** A bar held by the focus or the pointer stays held for the change it now shows. */
  #offerUndo(undo: StockUndo, ended: () => void, held: boolean): void {
    this.#clearUndoTimer();
    this.state.undo.value = undo;
    const undoWindow: UndoWindow = {
      endsAt: 0,
      left: UNDO_MS,
      held,
      ended: () => {
        if (this.state.undo.value !== undo) return;
        this.state.undo.value = null;
        ended();
      },
    };
    this.#undoWindow = undoWindow;
    if (!held) this.#runUndoWindow(undoWindow, UNDO_MS);
  }

  #runUndoWindow(undoWindow: UndoWindow, ms: number): void {
    undoWindow.endsAt = Date.now() + ms;
    this.#undoTimer = setTimeout(() => {
      this.#undoTimer = null;
      this.#undoWindow = null;
      undoWindow.ended();
    }, ms);
  }

  /** Ends the Undo window now: a waiting count is sent, a price stays as the lookup left it. */
  #endUndo(): void {
    const undo = this.state.undo.value;
    this.#clearUndoTimer();
    if (!undo) return;
    this.state.undo.value = null;
    if (undo.kind === "stock") void this.#commitStock(undo.cardId);
  }

  #clearUndoTimer(): void {
    if (this.#undoTimer !== null) clearTimeout(this.#undoTimer);
    this.#undoTimer = null;
    this.#undoWindow = null;
  }

  /** A write of this page on its way, kept until it settles. */
  #track(cardId: string, write: Promise<void>): Promise<void> {
    this.#writes.set(cardId, write);
    void write
      .catch(() => undefined)
      .then(() => {
        if (this.#writes.get(cardId) === write) this.#writes.delete(cardId);
      });
    return write;
  }

  /**
   * The list as it was left for a card, with the counts its rows showed, shown at once and held: the card page,
   * another Stock page or another visit may have changed any card, so nothing counts until the list is read again.
   */
  #restore(place: StockListPlace): void {
    const asked = ++this.#asked;
    this.state.items.value = place.items;
    this.state.nextCursor.value = place.nextCursor;
    this.state.stockEdits.value = place.stockEdits;
    for (const cardId of Object.keys(place.stockEdits)) this.#restoredEdits.add(cardId);
    this.state.status.value = loaded;
    this.#shown = { filter: place.filter, kind: place.kind, search: place.search };
    this.#place = { y: place.scrollY, cardId: place.openedCardId };
    this.state.scrollTo.value = this.#place;
    this.state.refreshing.value = true;
    this.#awaiting.push({
      after: asked,
      cards: null,
      release: () => {
        this.state.refreshing.value = false;
      },
    });
    void this.#refresh().catch(() => undefined);
  }

  /** Reads the list shown again, as many cards as it shows; the server answers at most a page of 250. */
  #refresh(): Promise<void> {
    const query = this.#query();
    if (!query) return Promise.resolve();
    const limit = Math.min(CARDS_PAGE_MAX, Math.max(CARDS_PAGE_SIZE, this.state.items.value.length));
    return this.#list.load({ ...query, limit });
  }

  /** A write may start on a row: for a role that may edit, while no card opens and the row is not held. */
  #mayWrite(card: CardSummary): boolean {
    return (
      this.state.canEditInventory.value &&
      this.state.opening.value === null &&
      !this.state.refreshing.value &&
      !this.state.busy.value.has(card.id)
    );
  }

  /** Holds a card's row until the release is called; a row waits while any hold on it lasts. */
  #hold(cardId: string): () => void {
    const of = this.#holdsOf;
    this.#holds.set(cardId, (this.#holds.get(cardId) ?? 0) + 1);
    this.state.busy.value = new Set(this.#holds.keys());
    return () => {
      if (of !== this.#holdsOf || this.isDisposed) return;
      const left = (this.#holds.get(cardId) ?? 1) - 1;
      if (left > 0) this.#holds.set(cardId, left);
      else this.#holds.delete(cardId);
      this.state.busy.value = new Set(this.#holds.keys());
    };
  }

  /** Rows whose writes outlast the list's wait stay held; once those have all landed, the list is read again. */
  #holdForWrites(shopId: string, pending: ReadonlyMap<string, Promise<void>>): void {
    // A wait for a list of another shop: its rows are not on screen, and nothing would let the holds go.
    if (!this.#current(shopId)) return;
    const cards = [...pending.keys()].filter((cardId) => !this.#series.has(cardId));
    if (cards.length === 0) return;
    const releases = cards.map((cardId) => this.#hold(cardId));
    const awaiting: Awaiting = {
      after: Number.POSITIVE_INFINITY,
      cards: new Set(cards),
      release: () => {
        for (const release of releases) release();
      },
    };
    this.#awaiting.push(awaiting);
    void landed([...pending.values()]).then(() => {
      if (!this.#current(shopId)) return;
      awaiting.after = ++this.#asked;
      void this.#refresh().catch(() => undefined);
    });
  }

  /**
   * A write whose answer was lost, on a card nothing has read since: its row waits for a list read asked after now,
   * asked at once, so no tap counts from a copy the write may have changed.
   */
  #holdUntilListRead(shopId: string, cardId: string): void {
    if (!this.#current(shopId)) return;
    this.#awaiting.push({ after: ++this.#asked, cards: new Set([cardId]), release: this.#hold(cardId) });
    void this.#refresh().catch(() => undefined);
  }

  /** A list read asked after what a hold waits for lets it go; the counts Back showed go once a read covers them. */
  #settleAwaiting(asked: number): void {
    const settled = this.#awaiting.filter((a) => a.after < asked);
    if (settled.length === 0) return;
    this.#awaiting = this.#awaiting.filter((a) => a.after >= asked);
    for (const a of settled) a.release();
    const waiting = new Set(this.#awaiting.flatMap((a) => [...(a.cards ?? [])]));
    let edits = this.state.stockEdits.value;
    for (const cardId of [...this.#restoredEdits]) {
      if (waiting.has(cardId)) continue;
      this.#restoredEdits.delete(cardId);
      edits = without(edits, cardId);
    }
    this.state.stockEdits.value = edits;
  }

  /** The tap waiting for its window goes: the row shows the newest count sent while it is on its way, else the server's. */
  #dropTap(cardId: string, series: StockSeries): void {
    const edits = this.state.stockEdits.value;
    if (series.counted !== null && this.#stockSaves.has(cardId)) {
      this.state.stockEdits.value = { ...edits, [cardId]: series.counted };
      return;
    }
    this.#series.delete(cardId);
    this.state.stockEdits.value = without(edits, cardId);
  }

  /** Sends one card's count after any save still running for it, then reads that card again: the list keeps its pages. */
  #commitStock(cardId: string): Promise<void> {
    const shopId = this.#shopId;
    const target = this.state.stockEdits.value[cardId];
    const series = this.#series.get(cardId);
    if (shopId === null || target === undefined || !series) return Promise.resolve();
    series.counted = target;
    const sent = ++series.sent;
    const previous = this.#stockSaves.get(cardId);
    const send = () => this.#saveStock({ shopId, cardId, target, series, sent });
    const save = previous ? previous.catch(() => undefined).then(send) : send();
    this.#stockSaves.set(cardId, save);
    void save.finally(() => {
      if (this.#stockSaves.get(cardId) === save) this.#stockSaves.delete(cardId);
    });
    return save;
  }

  async #saveStock({ shopId, cardId, target, series, sent }: StockSave): Promise<void> {
    // Sent even once the page is gone or the shop changed: the count was made, only its answer has nowhere to show.
    try {
      const r = await setCardStock(this.#stockChange(shopId, cardId, series, target)).catch(async (thrown: unknown) => {
        if (!this.#current(shopId)) throw thrown;
        const read = await this.#readCard(shopId, cardId).catch(() => false);
        // Another shop chosen while the card was read: this is not its page.
        if (!this.#current(shopId)) throw thrown;
        this.state.error.value = stockFailedText(series.title, read);
        if (!read) this.#holdUntilListRead(shopId, cardId);
        throw thrown;
      });
      if (!this.#current(shopId)) return;
      if (!r.ok) this.state.error.value = errorText(r.errors);
      else this.#patch(cardId, (c) => ({ ...c, onHand: r.value.onHand, available: target }));
      await this.#readCard(shopId, cardId).catch(() => false);
    } finally {
      if (this.#current(shopId)) this.#settleStock(cardId, series, sent);
    }
  }

  /** The newest count of a series landed: the row shows what the server holds, unless a tap waits for its window. */
  #settleStock(cardId: string, series: StockSeries, sent: number): void {
    if (this.#series.get(cardId) !== series || series.sent !== sent) return;
    if (this.state.stockEdits.value[cardId] !== series.counted) return;
    this.#series.delete(cardId);
    this.state.stockEdits.value = without(this.state.stockEdits.value, cardId);
  }

  /** A count of a series is sent against the card as the row showed it at the first tap, whatever was read since. */
  #stockChange(shopId: string, cardId: string, series: StockSeries, target: number) {
    return {
      ...this.deps,
      shopId,
      cardId,
      change: { quantity: onHandFor(series.base, target), kind: "ADJUSTED" as const, reason: STOCK_LIST_REASON },
    };
  }

  /** On the way out the count is sent with nothing to show its answer to; the next page waits for what is on its way. */
  #leave(): void {
    const writes = this.#onTheWay();
    const undo = this.state.undo.value;
    this.#clearUndoTimer();
    const shopId = this.#shopId;
    const target = undo?.kind === "stock" ? this.state.stockEdits.value[undo.cardId] : undefined;
    const series = undo ? this.#series.get(undo.cardId) : undefined;
    if (undo && shopId !== null && target !== undefined && series) {
      const previous = this.#stockSaves.get(undo.cardId) ?? Promise.resolve();
      const save = previous
        .catch(() => undefined)
        .then(() => setCardStock(this.#stockChange(shopId, undo.cardId, series, target)));
      writes.set(undo.cardId, landed([writes.get(undo.cardId), save]));
    }
    for (const [cardId, write] of writes) this.deps.stockListRepository.leaving(cardId, write);
  }

  /** This page's writes on their way, per card. */
  #onTheWay(): Map<string, Promise<void>> {
    const writes = new Map<string, Promise<void>>();
    for (const cardId of new Set([...this.#stockSaves.keys(), ...this.#writes.keys()]))
      writes.set(cardId, landed([this.#stockSaves.get(cardId), this.#writes.get(cardId)]));
    return writes;
  }

  /**
   * The writes on their way, this page's and those pages left, landed: null when none is. The list waits for them
   * a while at most; then the cards still being written wait on their rows, unless this page holds the count.
   */
  #writesLanded(shopId: string): Promise<void> | null {
    const pending = new Map(this.deps.stockListRepository.left);
    for (const [cardId, write] of this.#onTheWay()) pending.set(cardId, landed([pending.get(cardId), write]));
    if (pending.size === 0) return null;
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        resolve();
        this.#holdForWrites(shopId, pending);
      }, OPEN_WAITS_MS);
      for (const [cardId, write] of pending)
        void write.then(() => {
          pending.delete(cardId);
          if (pending.size > 0) return;
          clearTimeout(timer);
          resolve();
        });
    });
  }

  /** Reads one card; true once its answer is known (a card the shop no longer has leaves the list). */
  async #readCard(shopId: string, cardId: string): Promise<boolean> {
    const asked = ++this.#asked;
    const r = await loadCard({ ...this.deps, shopId, cardId });
    if (!this.#current(shopId)) return false;
    if (!r.ok) {
      if (!hasError(r, CardNotFound)) return false;
      this.#learn(cardId, asked, null);
      return true;
    }
    const { card, offerId, channel, externalProductId, providerPriceCents } = r.value;
    this.#learn(cardId, asked, { card, offerId, channel, externalProductId, providerPriceCents });
    return true;
  }

  /**
   * What a write answered, shown at once and laid over the card's copy, so neither a read that fails after it nor one
   * sent before it lands puts the old figure back; the next read sent after the answer covers it.
   */
  #patch(cardId: string, change: (card: CardSummary) => CardSummary): void {
    const known = this.#known.get(cardId);
    if (!known || known.read === null) return;
    const next = { ...known, laid: [...known.laid, { at: ++this.#asked, change }] };
    this.#known.set(cardId, next);
    this.#show(cardId, shownOf(next));
  }

  /** A read's answer about one card, sent at `asked`: none sent later has answered, so its copy is the newest. */
  #learn(cardId: string, asked: number, read: CardListItem | null): void {
    const next = this.#seen(cardId, asked, read);
    if (next) this.#show(cardId, shownOf(next));
  }

  /** The newest read about a card, with the writes answered since it went out still laid on it; null when older. */
  #seen(cardId: string, asked: number, read: CardListItem | null): Known | null {
    const known = this.#known.get(cardId);
    if (known && known.asked > asked) return null;
    const next: Known = { asked, read, laid: known?.laid.filter((l) => l.at > asked) ?? [] };
    this.#known.set(cardId, next);
    return next;
  }

  #show(cardId: string, item: CardListItem | null): void {
    const items = this.state.items.value;
    if (!items.some((i) => i.card.id === cardId)) return;
    this.state.items.value = item
      ? items.map((i) => (i.card.id === cardId ? item : i))
      : items.filter((i) => i.card.id !== cardId);
  }

  #landPage(page: CardPage, query: CardsQuery): void {
    const cards = page.cards.flatMap((item) => {
      // A card read alone after this page went out is newer than the page's copy of it.
      const known = this.#seen(item.card.id, query.asked, item) ?? this.#known.get(item.card.id);
      const shown = known ? shownOf(known) : null;
      return shown ? [shown] : [];
    });
    this.state.items.value = query.cursor ? [...this.state.items.value, ...cards] : cards;
    this.state.nextCursor.value = page.nextCursor;
    this.#shown = { filter: query.filter, kind: query.kind, search: query.search };
    if (!query.cursor) {
      this.#place = null;
      this.#settleAwaiting(query.asked);
    }
  }

  #setOutcome(cardId: string, outcome: RepriceOutcome): void {
    this.state.outcomes.value = { ...this.state.outcomes.value, [cardId]: outcome };
  }

  /** Whether an answer for this shop may still change the page. */
  #current(shopId: string): boolean {
    return !this.isDisposed && shopId === this.#shopId;
  }

  async #load(): Promise<void> {
    const query = this.#query();
    if (query) await this.#list.load(query);
  }

  /** Null until the shop is known. */
  #query(): CardsQuery | null {
    if (this.#shopId === null) return null;
    return {
      shopId: this.#shopId,
      filter: this.state.filter.value,
      kind: this.state.kind.value,
      search: this.state.search.value,
      asked: ++this.#asked,
    };
  }
}
